/**
 * File Upload Component
 * Handles drag & drop file uploads with preview
 */
class FileUpload {
  constructor (element) {
    // Prevent double initialization - if element already has a FileUpload instance, return it
    if (element.fileUpload) {
      console.warn('[FileUpload] Element already has a FileUpload instance, returning existing instance');
      return element.fileUpload;
    }

    // Store instance on element IMMEDIATELY to prevent race conditions
    // This must happen before init() to prevent double-binding
    element.fileUpload = this;

    this.element = element;
    this.input = element.querySelector('input[type="file"]');
    this.multiple = this.input.hasAttribute('multiple');
    this.acceptedTypes = this.input.getAttribute('accept');
    this.files = [];
    this.activeXHR = null; // Store active XMLHttpRequest for cancellation
    this.activeXHRs = new Map(); // Store multiple active XHRs keyed by file reference (not index!)

    // Notification options
    this.showNotifications = element.dataset.showNotifications === 'true';
    this.notificationPosition = element.dataset.notificationPosition || 'top-right';
    this.notificationPersistent = element.dataset.notificationPersistent !== 'false';
    this.fileNotifications = new Map(); // filename -> notificationId

    // Store bound handler references so they can be removed later
    this.boundHandlers = {
      click: null,
      change: null,
      dragover: null,
      dragleave: null,
      drop: null,
    };

    this.init();
  }

  /**
   * Navigate to a specific wizard step (compatible with submit-title form)
   */
  navigateToStep (stepNumber) {
    // Try multiple navigation methods for compatibility
    if (window.submitTitle?.wizard?.goToStep) {
      window.submitTitle.wizard.goToStep(stepNumber);
    } else if (window.submitTitle?.showStep) {
      window.submitTitle.showStep(stepNumber);
    } else {
      console.warn('No navigation method available for step:', stepNumber);
    }
  }

  init () {
    // Bind events
    this.bindEvents();

    // Create file list container if multiple
    if (this.multiple) {
      this.createFileList();
    }
  }

  unbindEvents () {
    // Remove all event listeners using stored references
    if (this.boundHandlers.click) {
      this.element.removeEventListener('click', this.boundHandlers.click);
    }
    if (this.boundHandlers.change) {
      this.input.removeEventListener('change', this.boundHandlers.change);
    }
    if (this.boundHandlers.dragover) {
      this.element.removeEventListener('dragover', this.boundHandlers.dragover);
    }
    if (this.boundHandlers.dragleave) {
      this.element.removeEventListener('dragleave', this.boundHandlers.dragleave);
    }
    if (this.boundHandlers.drop) {
      this.element.removeEventListener('drop', this.boundHandlers.drop);
    }
  }

  bindEvents () {
    // Unbind any existing handlers first to prevent double-binding
    this.unbindEvents();

    // Click to browse - store reference
    this.boundHandlers.click = (e) => {
      if (e.target.closest('.file-remove')) return;
      // Prevent handling clicks that originated from the file input itself (bubbled up)
      if (e.target === this.input) return;
      this.input.click();
    };
    this.element.addEventListener('click', this.boundHandlers.click);

    // File input change - store reference
    this.boundHandlers.change = (e) => {
      this.handleFiles(e.target.files);
    };
    this.input.addEventListener('change', this.boundHandlers.change);

    // Drag events - store references
    this.boundHandlers.dragover = (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.element.classList.add('drag-over');
    };
    this.element.addEventListener('dragover', this.boundHandlers.dragover);

    this.boundHandlers.dragleave = (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.element.classList.remove('drag-over');
    };
    this.element.addEventListener('dragleave', this.boundHandlers.dragleave);

    this.boundHandlers.drop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.element.classList.remove('drag-over');

      const files = e.dataTransfer.files;
      this.handleFiles(files);
    };
    this.element.addEventListener('drop', this.boundHandlers.drop);
  }

  handleFiles (files) {
    const fileArray = Array.from(files);

    // Initialize file tracking properties for new files
    fileArray.forEach(file => {
      // Add unique ID for stable tracking (survives array index changes)
      if (!file._fileUploadId) {
        file._fileUploadId = `file-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      }
      file.uploadProgress = 0;
      file.uploadStatus = 'pending';
      file.uploaded = false;
    });

    if (!this.multiple) {
      // Single file mode
      this.files = fileArray.slice(0, 1);
    } else {
      // Multiple file mode
      this.files = this.files.concat(fileArray);
    }

    // Validate files
    const originalLength = this.files.length;
    this.files = this.files.filter(file => this.validateFile(file));

    if (originalLength > 0 && this.files.length === 0) {
      console.error('All files were rejected during validation');
    }

    // Update UI
    this.updateUI();

    // Create notifications for new files if enabled
    if (this.showNotifications && window.StudioDome?.notify) {
      fileArray.forEach(file => {
        if (this.validateFile(file)) {
          const notificationId = this.createFileNotification(file, 'preparing');
          this.fileNotifications.set(file.name, notificationId);
        }
      });
    }

    // Trigger custom event
    this.element.dispatchEvent(new CustomEvent('fileselect', {
      bubbles: true,
      detail: { files: this.files },
    }));

    // Auto-upload if upload URL is provided
    const uploadUrl = this.element.getAttribute('data-upload-url');
    if (uploadUrl && this.files.length > 0) {
      console.log('🚀 AUTO-UPLOAD: Starting auto-upload to:', uploadUrl);
      console.log('🚀 AUTO-UPLOAD: File count:', this.files.length);
      console.log('🚀 AUTO-UPLOAD: Multiple mode:', this.multiple);

      // For single file mode, trigger upload immediately
      if (!this.multiple) {
        // Try to find the upload button
        setTimeout(() => {
          const uploadBtn = this.element.parentNode.querySelector('.file-upload-btn');
          console.log('🚀 AUTO-UPLOAD: Upload button search result:', uploadBtn ? 'FOUND' : 'NOT FOUND');

          if (uploadBtn) {
            console.log('🚀 AUTO-UPLOAD: Clicking upload button programmatically');
            uploadBtn.click();
          } else {
            console.log('🚀 AUTO-UPLOAD: No button found, calling autoUploadSingleFile directly');
            // If no button found (UI not ready), upload directly
            this.autoUploadSingleFile(uploadUrl);
          }
        }, 100);
      } else {
        // For multiple files in auto-upload mode, upload all files
        console.log('🚀 AUTO-UPLOAD: Multiple mode detected, auto-uploading ALL files');
        setTimeout(() => {
          // Auto-upload all files with progress tracking
          this.autoUploadAllFiles(uploadUrl, fileArray);
        }, 100);
      }
    }
  }

  async autoUploadFirstFile (uploadUrl) {
    console.log('🔥 AUTO-UPLOAD MULTIPLE: autoUploadFirstFile called for multiple mode');
    console.log('🔥 AUTO-UPLOAD MULTIPLE: Upload URL:', uploadUrl);

    // IMMEDIATELY hide all upload buttons
    const immediateButtons = document.querySelectorAll('.file-upload-btn');
    immediateButtons.forEach(btn => {
      btn.style.display = 'none';
    });
    console.log(`🔥 AUTO-UPLOAD MULTIPLE: Immediately hid ${immediateButtons.length} upload buttons`);

    if (!this.files || this.files.length === 0) {
      console.error('🔥 AUTO-UPLOAD MULTIPLE: ERROR - No files to auto-upload');
      return;
    }

    const file = this.files[0];
    console.log('🔥 AUTO-UPLOAD MULTIPLE: Uploading first file:', file.name, 'Size:', file.size);

    // Show progress container on the main element
    let progressContainer = this.element.querySelector('.upload-progress');
    if (!progressContainer) {
      progressContainer = document.createElement('div');
      progressContainer.className = 'upload-progress';
      progressContainer.innerHTML = '<div class="progress-bar"></div>';
      this.element.appendChild(progressContainer);
      console.log('🔥 AUTO-UPLOAD MULTIPLE: Created progress container');
    }
    progressContainer.style.display = 'block';
    progressContainer.style.opacity = '1';
    progressContainer.style.visibility = 'visible';

    // Make sure the progress bar is visible
    const progressBar = progressContainer.querySelector('.progress-bar');
    if (progressBar) {
      progressBar.style.width = '0%';
      progressBar.style.transition = 'width 0.3s ease';
      console.log('🔥 AUTO-UPLOAD MULTIPLE: Progress bar initialized');
    }

    // Mark element as uploading
    this.element.classList.add('uploading');

    // Update the text to show uploading with fade transition
    const text = this.element.querySelector('.upload-text, p');
    if (text) {
      const originalText = text.innerHTML;
      text.setAttribute('data-original-text', originalText);
    }

    // Hide ALL upload buttons - both in file item and anywhere else
    const allUploadButtons = document.querySelectorAll('.file-upload-btn');
    allUploadButtons.forEach(btn => {
      btn.style.display = 'none';
      console.log('🔥 AUTO-UPLOAD MULTIPLE: Hid upload button:', btn.className);
    });

    // Also hide buttons in the specific file item
    const fileItem = this.element.parentNode.querySelector('.single-file-item');
    if (fileItem) {
      const uploadBtn = fileItem.querySelector('.file-upload-btn');
      if (uploadBtn) {
        uploadBtn.style.display = 'none';
        console.log('🔥 AUTO-UPLOAD MULTIPLE: Hid file item upload button');
      }

      const removeBtn = fileItem.querySelector('.file-remove');
      if (removeBtn) {
        // Keep remove button visible but update its behavior during upload
        removeBtn.style.display = 'inline-flex';

        // Add click handler to cancel upload
        const cancelHandler = (e) => {
          e.stopPropagation();
          if (this.activeXHR) {
            console.log('🔥 AUTO-UPLOAD MULTIPLE: User cancelled upload');
            this.activeXHR.abort();
            this.activeXHR = null;

            // Clean up UI
            this.element.classList.remove('uploading', 'processing');
            const progressBar = this.element.querySelector('.upload-progress');
            if (progressBar) {
              progressBar.style.display = 'none';
            }

            // Restore text
            const text = this.element.querySelector('.upload-text, p');
            if (text && text.hasAttribute('data-original-text')) {
              text.innerHTML = text.getAttribute('data-original-text');
              text.removeAttribute('data-original-text');
              text.classList.remove('pulsate', 'fade-transition');
            }

            // Remove the file and clean up
            this.removeFile(0);
            fileItem.remove();
            this.element.classList.remove('has-file', 'is-valid', 'is-invalid');

            // Remove this handler
            removeBtn.removeEventListener('click', cancelHandler);
          }
        };

        removeBtn.addEventListener('click', cancelHandler);
        console.log('🔥 AUTO-UPLOAD MULTIPLE: Added cancel handler to remove button');
      }

      // Show progress bar in file item if it exists
      const fileProgress = fileItem.querySelector('.file-progress');
      if (fileProgress) {
        fileProgress.style.display = 'block';
        console.log('🔥 AUTO-UPLOAD MULTIPLE: Showing file item progress bar');
      }
    }

    try {
      // Call the main upload method with progress callback
      const result = await this.upload(uploadUrl, (progress) => {
        console.log(`🔥 AUTO-UPLOAD MULTIPLE: Progress callback - ${progress}%`);
        // Progress is already handled by upload method
      });

      console.log('🔥 AUTO-UPLOAD MULTIPLE: Upload successful:', result);

      // Update text to show success with fade
      const successText = this.element.querySelector('.upload-text, p');
      if (successText) {
        // Remove pulsate if it's still there
        successText.classList.remove('pulsate');
        successText.classList.add('fade-transition');

        setTimeout(() => {
          successText.innerHTML = 'Done!';
        }, 300);

        setTimeout(() => {
          successText.classList.remove('fade-transition');
        }, 600);
      }

      // Hide progress bar if still visible
      const progressToHide = this.element.querySelector('.upload-progress');
      if (progressToHide && progressToHide.style.display !== 'none') {
        progressToHide.style.transition = 'opacity 0.3s ease-out';
        progressToHide.style.opacity = '0';
        setTimeout(() => {
          progressToHide.style.display = 'none';
        }, 300);
      }

      // After showing "Done!", restore original text
      setTimeout(() => {
        // Restore original text with fade
        if (successText && successText.hasAttribute('data-original-text')) {
          successText.classList.add('fade-transition');

          setTimeout(() => {
            successText.innerHTML = successText.getAttribute('data-original-text');
            successText.removeAttribute('data-original-text');
          }, 300);

          setTimeout(() => {
            successText.classList.remove('fade-transition');
          }, 600);
        }

        // Remove uploading/processing classes
        this.element.classList.remove('uploading', 'processing');
        this.element.classList.add('success');

        // Restore remove button visibility (but not upload since it's done)
        const fileItemRestore = this.element.parentNode.querySelector('.single-file-item');
        if (fileItemRestore) {
          const removeBtn = fileItemRestore.querySelector('.file-remove');
          if (removeBtn) {
            removeBtn.style.display = 'inline-flex';
            // Remove any cancel handlers and restore normal remove behavior
            const newRemoveBtn = removeBtn.cloneNode(true);
            removeBtn.parentNode.replaceChild(newRemoveBtn, removeBtn);
            newRemoveBtn.addEventListener('click', (e) => {
              e.stopPropagation();

              // Clear the files array and dispatch fileremove event
              this.files = [];
              this.element.dispatchEvent(new CustomEvent('fileremove', {
                bubbles: true,
                detail: { files: [] },
              }));

              // Remove UI elements
              fileItemRestore.remove();
              this.element.classList.remove('has-file', 'success', 'is-valid', 'is-invalid');

              // Restore original text
              const text = this.element.querySelector('.upload-text, p');
              if (text) {
                const defaultText = this.element.querySelector('p:not(.upload-text)')?.textContent ||
                  this.element.dataset.placeholder ||
                  'Drag & drop files here or click to browse';
                text.innerHTML = defaultText;
              }
            });
          }
        }
      }, 1500); // Show "Done!" for 1.5 seconds

      // Dispatch event for successful upload
      this.element.dispatchEvent(new CustomEvent('fileuploaded', {
        bubbles: true,
        detail: {
          file: file,
          response: result,
        },
      }));

      return result;
    } catch (error) {
      console.error('🔥 AUTO-UPLOAD MULTIPLE: Upload failed:', error);

      // Clean up UI on error
      this.element.classList.remove('uploading', 'processing');
      this.element.classList.add('error');

      // Hide progress bar
      const progressToHide = this.element.querySelector('.upload-progress');
      if (progressToHide) {
        progressToHide.style.display = 'none';
      }

      // Restore text
      const text = this.element.querySelector('.upload-text, p');
      if (text && text.hasAttribute('data-original-text')) {
        text.innerHTML = text.getAttribute('data-original-text');
        text.removeAttribute('data-original-text');
        text.classList.remove('pulsate', 'fade-transition');
      }

      // Show error notification
      if (this.showNotifications && this.files[0] && error.message !== 'Upload cancelled') {
        this.updateFileNotification(this.files[0].name, 'error');
      }

      // Show error message
      this.showError('Upload failed: ' + error.message);

      throw error;
    }
  }

  async autoUploadSingleFile (uploadUrl) {
    console.log('🔥 DIRECT AUTO-UPLOAD: autoUploadSingleFile called');
    console.log('🔥 DIRECT AUTO-UPLOAD: Upload URL:', uploadUrl);

    if (!this.files || this.files.length === 0) {
      console.error('🔥 DIRECT AUTO-UPLOAD: ERROR - No files to auto-upload');
      return;
    }

    const file = this.files[0];
    console.log('🔥 DIRECT AUTO-UPLOAD: Uploading file:', file.name, 'Size:', file.size);

    // Show the progress bar immediately
    let progressContainer = this.element.querySelector('.upload-progress');
    if (!progressContainer) {
      progressContainer = document.createElement('div');
      progressContainer.className = 'upload-progress';
      progressContainer.innerHTML = '<div class="progress-bar"></div>';
      this.element.appendChild(progressContainer);
      console.log('Created progress container for auto-upload');
    }
    progressContainer.style.display = 'block';

    // Mark element as uploading
    this.element.classList.add('uploading');

    // Call upload method which handles processing state
    try {
      const result = await this.upload(uploadUrl);
      console.log('Auto-upload successful:', result);

      // Dispatch event for successful upload
      this.element.dispatchEvent(new CustomEvent('fileuploaded', {
        bubbles: true,
        detail: {
          file: file,
          response: result,
        },
      }));

      return result;
    } catch (error) {
      console.error('Auto-upload failed:', error);

      // Clean up UI on error
      this.element.classList.remove('uploading', 'processing');
      this.element.classList.add('error');

      // Hide progress bar
      progressContainer.style.display = 'none';

      // Show error notification
      if (this.showNotifications && this.files[0] && error.message !== 'Upload cancelled') {
        this.updateFileNotification(this.files[0].name, 'error');
      }

      // Show error message
      this.showError('Upload failed: ' + error.message);

      throw error;
    }
  }

  /**
   * Auto-upload all files in batch mode (for multiple file uploads)
   */
  async autoUploadAllFiles (uploadUrl, fileArray) {
    console.log('🔥 AUTO-UPLOAD ALL: Starting batch upload for multiple files');
    console.log('🔥 AUTO-UPLOAD ALL: Upload URL:', uploadUrl);
    console.log('🔥 AUTO-UPLOAD ALL: File count:', fileArray.length);

    if (!fileArray || fileArray.length === 0) {
      console.error('🔥 AUTO-UPLOAD ALL: ERROR - No files to upload');
      return;
    }

    // Store original text for restoration later
    const text = this.element.querySelector('.upload-text, p');
    if (text) {
      const originalText = text.innerHTML;
      text.setAttribute('data-original-text', originalText);
    }

    // Show overall progress bar
    let progressContainer = this.element.querySelector('.upload-progress');
    if (!progressContainer) {
      progressContainer = document.createElement('div');
      progressContainer.className = 'upload-progress';
      progressContainer.innerHTML = '<div class="progress-bar"></div>';
      this.element.appendChild(progressContainer);
    }
    progressContainer.style.display = 'block';

    const progressBar = progressContainer.querySelector('.progress-bar');

    // Store reference to progress bar for real-time updates
    this.batchProgressBar = progressBar;

    // Store reference to files in this batch for progress calculation
    this.batchFiles = fileArray;

    // Mark element as uploading
    this.element.classList.add('uploading');

    // Initialize progress tracking for files in this batch only
    fileArray.forEach(file => {
      file.uploadProgress = 0;
      file.uploadStatus = 'pending';
    });

    console.log(`🚀 Starting batch upload of ${fileArray.length} files`);

    let successCount = 0;
    let errorCount = 0;
    let cancelledCount = 0;

    // Upload files in parallel batches (max 3 at a time)
    const batchSize = 3;
    const totalFiles = fileArray.length;

    for (let i = 0; i < fileArray.length; i += batchSize) {
      const batch = fileArray.slice(i, i + batchSize);
      const batchPromises = batch.map(async (file, batchIndex) => {
        const batchFileIndex = i + batchIndex;

        // Find the actual index of this file in this.files array
        const actualIndex = this.files.indexOf(file);

        if (actualIndex === -1) {
          console.error(`🔥 AUTO-UPLOAD ALL: ERROR - File not found in files array:`, file.name);
          return { success: false, file: file.name, error: new Error('File not found') };
        }

        const fileItem = this.fileList?.querySelector(`[data-file-index="${actualIndex}"]`);

        try {
          console.log(`🔥 AUTO-UPLOAD ALL: Uploading file ${batchFileIndex + 1}/${totalFiles}: ${file.name} (index ${actualIndex})`);

          // Update file item UI if it exists
          if (fileItem) {
            fileItem.classList.add('uploading');
            const fileProgress = fileItem.querySelector('.file-progress');
            if (fileProgress) {
              fileProgress.style.display = 'block';
            }
          }

          // Upload the file using the existing uploadSingleFileInBatch method with the actual index
          await this.uploadSingleFileInBatch(actualIndex);

          successCount++;

          // uploadSingleFileInBatch already set progress to 100 and called updateBatchProgress
          return { success: true, file: file.name };
        } catch (error) {
          // uploadSingleFileInBatch already handled the file status and progress update
          // Just track counts for final reporting
          if (error.message === 'Upload cancelled') {
            cancelledCount++;
            console.log(`🔥 AUTO-UPLOAD ALL: Upload cancelled for ${file.name}`);
          } else {
            errorCount++;
            console.error(`🔥 AUTO-UPLOAD ALL: Failed to upload ${file.name}:`, error);
          }

          return { success: false, file: file.name, error };
        }
      });

      // Wait for current batch to complete before starting next
      await Promise.all(batchPromises);
    }

    console.log('🎉 All batches complete! Showing final 100% progress...');

    // Ensure progress shows 100% before hiding
    progressBar.style.width = '100%';
    this.element.style.background = `linear-gradient(90deg, rgba(76, 175, 80, 0.1) 0%, rgba(76, 175, 80, 0.1) 100%, transparent 100%)`;

    // Wait a moment to show 100% completion before hiding
    await new Promise(resolve => setTimeout(resolve, 300));

    console.log('Hiding progress bar after showing 100%...');

    // Hide progress bar and reset background
    progressContainer.style.transition = 'opacity 0.3s ease-out';
    progressContainer.style.opacity = '0';
    setTimeout(() => {
      progressContainer.style.display = 'none';
    }, 300);

    // Clear the background gradient and batch progress references
    this.element.style.background = '';
    this.batchProgressBar = null;
    this.batchFiles = null;

    // Update final status
    this.element.classList.remove('uploading', 'processing');

    if (errorCount === 0 && cancelledCount === 0) {
      // All files succeeded
      this.element.classList.add('success');
      if (text) {
        text.innerHTML = `All ${totalFiles} file${totalFiles > 1 ? 's' : ''} uploaded!`;
      }

      // Restore original text after delay
      setTimeout(() => {
        if (text && text.hasAttribute('data-original-text')) {
          text.innerHTML = text.getAttribute('data-original-text');
          text.removeAttribute('data-original-text');
        }
        this.element.classList.remove('success');
      }, 3000);
    } else if (errorCount > 0) {
      // Some files failed (errors take priority over cancellations in messaging)
      this.element.classList.add('error');
      const statusParts = [];
      if (successCount > 0) statusParts.push(`${successCount} uploaded`);
      if (cancelledCount > 0) statusParts.push(`${cancelledCount} cancelled`);
      if (errorCount > 0) statusParts.push(`${errorCount} failed`);

      if (text) {
        text.innerHTML = statusParts.join(', ');
      }

      this.showError(`${errorCount} file(s) failed to upload`);

      // Restore original text after delay
      setTimeout(() => {
        if (text && text.hasAttribute('data-original-text')) {
          text.innerHTML = text.getAttribute('data-original-text');
          text.removeAttribute('data-original-text');
        }
        this.element.classList.remove('error');
      }, 5000);
    } else if (cancelledCount > 0) {
      // Only cancellations, no errors
      if (text) {
        const statusParts = [];
        if (successCount > 0) statusParts.push(`${successCount} uploaded`);
        statusParts.push(`${cancelledCount} cancelled`);
        text.innerHTML = statusParts.join(', ');
      }

      // Restore original text after delay
      setTimeout(() => {
        if (text && text.hasAttribute('data-original-text')) {
          text.innerHTML = text.getAttribute('data-original-text');
          text.removeAttribute('data-original-text');
        }
      }, 3000);
    }

    console.log(`🔥 AUTO-UPLOAD ALL: Batch upload complete. Success: ${successCount}, Cancelled: ${cancelledCount}, Errors: ${errorCount}`);

    // Dispatch event for batch completion
    this.element.dispatchEvent(new CustomEvent('batchuploaded', {
      bubbles: true,  // Enable bubbling so jQuery delegation can catch it
      detail: {
        total: totalFiles,
        success: successCount,
        cancelled: cancelledCount,
        errors: errorCount,
      },
    }));
  }

  validateFile (file) {
    // Check file type if accept attribute is set
    if (this.acceptedTypes) {
      const acceptedExtensions = this.acceptedTypes.split(',').map(type => type.trim());

      const fileType = file.type;
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();

      const isAccepted = acceptedExtensions.some(accepted => {
        // Check if it's a MIME type pattern (e.g., video/*)
        if (accepted.includes('/')) {
          if (accepted.endsWith('/*')) {
            // Wildcard MIME type (e.g., video/*)
            return fileType.startsWith(accepted.replace('/*', '/'));
          } else {
            // Exact MIME type match
            return fileType === accepted;
          }
        } else if (accepted.startsWith('.')) {
          // File extension match (e.g., .zip, .mp4)
          return fileExtension === accepted.toLowerCase();
        }
        return false;
      });

      // Special handling for ZIP files which might have various MIME types
      if (!isAccepted && fileExtension === '.zip') {
        // Check if .zip is in the accepted extensions
        const hasZipExtension = acceptedExtensions.some(ext => ext.toLowerCase() === '.zip');
        if (hasZipExtension) {
          console.log('ZIP file detected with non-standard MIME type, allowing based on extension');
          return true;
        }
      }

      if (!isAccepted) {
        this.showError(`File type not accepted: ${file.name}`);
        console.error(`File validation failed: ${file.name}`, {
          fileType: fileType,
          fileExtension: fileExtension,
          acceptedTypes: this.acceptedTypes,
        });
        return false;
      }
    }

    // Check file size - read from data attribute or use default
    const maxSize = parseInt(this.element.dataset.maxSize) || 104857600;
    if (file.size > maxSize) {
      const maxSizeFormatted = this.formatFileSize(maxSize);
      const fileSizeFormatted = this.formatFileSize(file.size);
      this.showError(`File too large: ${file.name} (${fileSizeFormatted}). Maximum allowed: ${maxSizeFormatted}`);
      return false;
    }

    return true;
  }

  updateUI () {
    if (!this.multiple && this.files.length > 0) {
      // Single file mode - create a file item display
      const file = this.files[0];

      // Store file reference to avoid closure issues
      const currentFile = file;

      // Remove any existing file item
      const existingItem = this.element.parentNode.querySelector('.single-file-item');
      if (existingItem) {
        existingItem.remove();
      }

      // Check if auto-upload is enabled
      const isAutoUpload = this.element.hasAttribute('data-upload-url');

      // Create file item below the upload area
      const fileItem = document.createElement('div');
      fileItem.className = 'file-item single-file-item';

      // Only show upload button if NOT auto-upload
      const uploadButtonHtml = !isAutoUpload ? `
          <button type="button" class="file-upload-btn button small primary mb-0">
            <i class="fas fa-upload"></i> Upload
          </button>` : '';

      fileItem.innerHTML = `
        <i class="file-icon ${this.getFileIcon(file)}"></i>
        <div class="file-info">
          <p class="file-name">${file.name}</p>
          <p class="file-size">${this.formatFileSize(file.size)}</p>
        </div>
        <div class="file-actions">
          ${uploadButtonHtml}
          <button type="button" class="file-remove button small mb-0">
            <i class="fas fa-times"></i>
          </button>
        </div>
      `;

      // Insert after the upload area
      this.element.parentNode.insertBefore(fileItem, this.element.nextSibling);

      // Add file status
      this.element.classList.add('has-file');

      // Bind upload button (only if it exists - not in auto-upload mode)
      const uploadBtn = fileItem.querySelector('.file-upload-btn');
      if (uploadBtn) {
        uploadBtn.addEventListener('click', async (e) => {
          e.stopPropagation();

          console.log('Upload button clicked');
          console.log('Current files array:', this.files);
          console.log('Files array length:', this.files.length);
          console.log('File reference from closure:', currentFile);

          // Use the closure file reference as a fallback
          if (this.files.length === 0 && currentFile) {
            console.warn('Files array was empty, using closure reference');
            this.files = [currentFile];
          }

          if (this.files.length === 0) {
            console.error('No files in array when upload button clicked!');
            this.showError('No file selected for upload');
            return;
          }

          try {
            // Hide the upload button immediately
            uploadBtn.style.display = 'none';
            fileItem.querySelector('.file-progress').style.display = 'block';

            const uploadUrl = this.element.dataset.uploadUrl || '/api/public/upload-file';
            const result = await this.upload(uploadUrl, (progress) => {
              const progressBar = fileItem.querySelector('.progress-bar');
              if (progressBar) {
                progressBar.style.width = progress + '%';
              }
            });

            // Update UI after successful upload
            fileItem.classList.add('success');
            uploadBtn.innerHTML = '<i class="fas fa-check"></i> Uploaded';
            uploadBtn.classList.remove('primary');
            uploadBtn.classList.add('success');
            uploadBtn.style.display = 'inline-flex';
            uploadBtn.disabled = true;

            fileItem.querySelector('.file-progress').style.display = 'none';

            // Dispatch custom event for parent components
            this.element.dispatchEvent(new CustomEvent('fileuploaded', {
              bubbles: true,
              detail: { file: file, result: result },
            }));

          } catch (error) {
            uploadBtn.style.display = 'inline-flex';
            fileItem.querySelector('.file-progress').style.display = 'none';
            fileItem.classList.add('error');
            console.error('Upload failed:', error);
          }
        });
      } // End of if (uploadBtn)

      // Bind remove button
      const removeBtn = fileItem.querySelector('.file-remove');
      removeBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        console.log('Remove button clicked for single file');

        // Cancel upload if in progress
        if (this.activeXHR) {
          console.log('Cancelling active upload...');
          this.activeXHR.abort();
          this.activeXHR = null;
        }

        // Store original text before clearing files
        const originalText = this.element.querySelector('p')?.textContent || 'Drag & drop files here or click to browse';

        // Remove the file from our array and dispatch event
        this.removeFile(0);  // This will dispatch the fileremove event

        // Now remove the UI element
        fileItem.remove();

        // Clean up UI states - IMPORTANT: Remove ALL validation-related classes
        this.element.classList.remove('has-file', 'success', 'uploading', 'processing', 'is-valid', 'is-invalid');

        // Hide progress bar
        const progressBar = this.element.querySelector('.upload-progress');
        if (progressBar) {
          progressBar.style.display = 'none';
        }

        // Restore original text
        const text = this.element.querySelector('.upload-text, p');
        if (text) {
          if (text.hasAttribute('data-original-text')) {
            text.innerHTML = text.getAttribute('data-original-text');
            text.removeAttribute('data-original-text');
            text.classList.remove('pulsate', 'fade-transition');
          } else if (text.innerHTML === 'Done!' || text.innerHTML.includes('Uploaded!')) {
            // Restore to the original upload area text
            const defaultText = this.element.querySelector('p:not(.upload-text)')?.textContent ||
              this.element.dataset.placeholder ||
              'Drag & drop files here or click to browse';
            text.innerHTML = defaultText;
          }
        }
      });
    }

    // Update file list for multiple mode
    if (this.multiple && this.fileList) {
      this.renderFileList();
    }
  }

  createFileList () {
    this.fileList = document.createElement('div');
    this.fileList.className = 'file-upload-list';
    this.element.parentNode.insertBefore(this.fileList, this.element.nextSibling);
  }

  renderFileList () {
    if (!this.fileList) return;

    this.fileList.innerHTML = '';

    // Check if auto-upload is enabled
    const isAutoUpload = this.element.hasAttribute('data-upload-url');

    // Add Upload All button if there are files AND not auto-upload
    if (this.files.length > 0 && !isAutoUpload) {
      const uploadAllDiv = document.createElement('div');
      uploadAllDiv.className = 'upload-all-container';
      uploadAllDiv.innerHTML = `
        <button type="button" class="upload-all-btn button primary">
          <i class="fas fa-upload"></i> Upload All Files
        </button>
      `;
      this.fileList.appendChild(uploadAllDiv);

      // Bind upload all button
      const uploadAllBtn = uploadAllDiv.querySelector('.upload-all-btn');
      uploadAllBtn.addEventListener('click', async (e) => {
        e.stopPropagation();
        await this.uploadAllFiles();
      });
    }

    // Sort files by status: uploading (top) → pending (middle) → uploaded (bottom) → cancelled/error (bottom)
    const statusOrder = { 'uploading': 0, 'pending': 1, 'uploaded': 2, 'cancelled': 3, 'error': 4 };
    const sortedFiles = this.files
      .map((file, index) => ({ file, index }))
      .sort((a, b) => {
        const aStatus = a.file.uploadStatus || 'pending';
        const bStatus = b.file.uploadStatus || 'pending';
        return (statusOrder[aStatus] || 1) - (statusOrder[bStatus] || 1);
      });

    sortedFiles.forEach(({ file, index }) => {
      const fileItem = document.createElement('div');
      fileItem.className = 'file-item';
      // Use stable file ID instead of array index for reliable DOM tracking
      fileItem.dataset.fileId = file._fileUploadId;
      fileItem.dataset.fileIndex = index; // Keep index for backwards compatibility

      // Add status-based classes
      const status = file.uploadStatus || 'pending';
      fileItem.classList.add(`status-${status}`);

      // Only show upload button if NOT auto-upload
      const uploadButtonHtml = !isAutoUpload ? `
          <button type="button" class="file-upload-btn button small primary mb-0" data-file-id="${file._fileUploadId}" data-index="${index}">
            <i class="fas fa-upload"></i> Upload
          </button>` : '';

      // Add status text based on file state
      let statusText = '';
      if (status === 'uploaded') {
        statusText = '<p class="file-status-text" style="font-size: 0.85em; color: #4caf50; margin-top: 4px;">✓ Uploaded</p>';
      } else if (status === 'cancelled') {
        statusText = '<p class="file-status-text" style="font-size: 0.85em; color: #999; margin-top: 4px;">✗ Cancelled</p>';
      } else if (status === 'error') {
        statusText = '<p class="file-status-text" style="font-size: 0.85em; color: #f44336; margin-top: 4px;">✗ Failed</p>';
      }

      fileItem.innerHTML = `
        <i class="file-icon ${this.getFileIcon(file)}"></i>
        <div class="file-info">
          <p class="file-name">${file.name}</p>
          <p class="file-size">${this.formatFileSize(file.size)}</p>
          ${statusText}
        </div>
        <div class="file-actions">
          ${uploadButtonHtml}
          <button type="button" class="file-remove button small mb-0" data-file-id="${file._fileUploadId}" data-index="${index}">
            <i class="fas fa-times"></i>
          </button>
        </div>
      `;

      // Set background gradient for upload progress if uploading (green color)
      if (status === 'uploading') {
        const progress = file.uploadProgress || 0;
        fileItem.style.background = `linear-gradient(90deg, rgba(76, 175, 80, 0.1) 0%, rgba(76, 175, 80, 0.1) ${progress}%, transparent ${progress}%)`;
      }

      this.fileList.appendChild(fileItem);
    });

    // Bind individual upload buttons (only if not auto-upload)
    if (!isAutoUpload) {
      this.fileList.querySelectorAll('.file-upload-btn').forEach(btn => {
        btn.addEventListener('click', async (e) => {
          e.stopPropagation();
          // Try to get file ID first, fall back to index for backwards compatibility
          const fileId = btn.dataset.fileId;
          let index = -1;

          if (fileId) {
            // Find file by ID (more reliable after removals)
            index = this.files.findIndex(f => f._fileUploadId === fileId);
          } else {
            // Fallback to index attribute
            index = parseInt(btn.dataset.index);
          }

          if (index >= 0 && index < this.files.length) {
            await this.uploadSingleFile(index);
          } else {
            console.error('Cannot find file to upload:', { fileId, index });
          }
        });
      });
    }

    // Bind remove buttons
    this.fileList.querySelectorAll('.file-remove').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        // Try to get file ID first, fall back to index for backwards compatibility
        const fileId = btn.dataset.fileId;
        let index = -1;
        let file = null;

        if (fileId) {
          // Find file by ID (more reliable after removals)
          index = this.files.findIndex(f => f._fileUploadId === fileId);
          file = this.files[index];
        } else {
          // Fallback to index attribute
          index = parseInt(btn.dataset.index);
          file = this.files[index];
        }

        if (!file) {
          console.warn('File not found at index:', index);
          return;
        }

        console.log(`🗑️ Remove button clicked for file: ${file.name} (index ${index})`);

        // Cancel upload if in progress for this file (using file reference as key)
        if (this.activeXHRs.has(file)) {
          console.log(`Aborting XHR for file: ${file.name}`);
          const xhr = this.activeXHRs.get(file);
          xhr.abort();
          this.activeXHRs.delete(file);
        } else {
          console.log(`No active XHR found for file: ${file.name}`);
        }

        this.removeFile(index);
      });
    });
  }

  removeFile (index) {
    // Store reference to the file being removed
    const removedFile = this.files[index];

    // Clean up notification for this file before removing
    if (this.showNotifications && removedFile) {
      this.removeFileNotification(removedFile.name);
    }

    // Cancel active upload if this file is currently uploading
    if (this.activeXHRs && this.activeXHRs.has(removedFile)) {
      const xhr = this.activeXHRs.get(removedFile);
      xhr.abort();
      this.activeXHRs.delete(removedFile);
      console.log(`🗑️ Cancelled upload for removed file: ${removedFile.name}`);
    }

    this.files.splice(index, 1);
    this.updateUI();

    // Clear input if no files left
    if (this.files.length === 0) {
      this.input.value = '';
      this.element.classList.remove('has-file');
    }

    // Recalculate batch progress if we're in a batch upload
    if (this.batchProgressBar && this.batchFiles) {
      console.log('🔄 Recalculating batch progress after file removal...');

      // Remove from batch files if it's there
      const batchIndex = this.batchFiles.indexOf(removedFile);
      if (batchIndex !== -1) {
        this.batchFiles.splice(batchIndex, 1);
        console.log(`Removed file from batch tracking. Remaining: ${this.batchFiles.length}`);
      }

      // Update progress calculation
      this.updateBatchProgress();

      // Check if we should start uploading pending files
      this.checkAndStartPendingUploads();
    }

    // Trigger custom event
    this.element.dispatchEvent(new CustomEvent('fileremove', {
      bubbles: true,
      detail: {
        files: this.files,
        removedFile: removedFile,
        removedIndex: index,
      },
    }));
  }

  /**
   * Check if there are pending files to upload and start them if slots are available
   * This is called after a file is removed during batch upload
   */
  checkAndStartPendingUploads () {
    if (!this.files || this.files.length === 0) {
      console.log('No files left to check for pending uploads');
      return;
    }

    // Count currently uploading files
    const uploadingCount = this.files.filter(f => f.uploadStatus === 'uploading').length;
    const batchSize = 3; // Same as in autoUploadAllFiles
    const availableSlots = batchSize - uploadingCount;

    console.log(`📊 Upload slots: ${uploadingCount} uploading, ${availableSlots} available`);

    if (availableSlots <= 0) {
      console.log('No available upload slots');
      return;
    }

    // Find pending files that need to be uploaded
    const pendingFiles = this.files
      .map((file, index) => ({ file, index }))
      .filter(({ file }) => file.uploadStatus === 'pending');

    if (pendingFiles.length === 0) {
      console.log('No pending files to upload');
      return;
    }

    // Start uploading pending files up to available slots
    const filesToStart = pendingFiles.slice(0, availableSlots);
    console.log(`🚀 Starting ${filesToStart.length} pending upload(s)...`);

    filesToStart.forEach(async ({ file, index }) => {
      try {
        await this.uploadSingleFileInBatch(index);
      } catch (error) {
        console.error(`Failed to start upload for ${file.name}:`, error);
      }
    });
  }

  getFileIcon (file) {
    const type = file.type.split('/')[0];
    switch (type) {
      case 'image':
        return 'fas fa-image';
      case 'video':
        return 'fas fa-video';
      case 'audio':
        return 'fas fa-music';
      case 'application':
        if (file.type.includes('pdf')) return 'fas fa-file-pdf';
        if (file.type.includes('zip') || file.type.includes('rar')) return 'fas fa-file-archive';
        if (file.type.includes('word')) return 'fas fa-file-word';
        if (file.type.includes('excel')) return 'fas fa-file-excel';
        return 'fas fa-file';
      default:
        return 'fas fa-file';
    }
  }

  formatFileSize (bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  showError (message) {
    this.element.classList.add('error');
    setTimeout(() => {
      this.element.classList.remove('error');
    }, 3000);

    // You can also show a toast notification here
    console.error(message);
  }

  // Upload functionality with progress tracking
  async upload (url, onProgress) {
    console.log('Upload called with files:', this.files);

    if (!this.files || this.files.length === 0) {
      this.showError('No files selected');
      console.error('Upload failed: No files in this.files array');
      return;
    }

    // Update notification to uploading state
    if (this.showNotifications && this.files[0]) {
      this.updateFileNotification(this.files[0].name, 'uploading');
    }

    const formData = new FormData();

    // Debug file object before adding to FormData
    console.log('About to add file to FormData:', {
      file: this.files[0],
      fileName: this.files[0]?.name,
      fileSize: this.files[0]?.size,
      fileType: this.files[0]?.type,
      fileConstructor: this.files[0]?.constructor?.name,
      isFile: this.files[0] instanceof File,
      isBlob: this.files[0] instanceof Blob,
    });

    // Validate that we have a proper File object
    if (this.files[0] && !(this.files[0] instanceof File || this.files[0] instanceof Blob)) {
      console.error('Invalid file object - not a File or Blob instance!');
      this.showError('Invalid file object');
      return;
    }

    // Check if we have an upload path specified (for GCS Manager)
    const uploadPath = this.element.dataset.uploadPath;
    if (uploadPath !== undefined) {
      formData.append('path', uploadPath);
      console.log('Added upload path to FormData:', uploadPath || 'root');
    }

    // Get the correct field name from data attribute or input name
    const inputName = this.element.dataset.inputName || this.input.name || 'file';

    // For single file mode, use the correct input name
    if (!this.multiple && this.files.length === 1) {
      // Try different approaches to ensure file is added
      try {
        formData.append('file', this.files[0], this.files[0].name);
        console.log(`Appended file to FormData with key "file" and filename`);
      } catch (e) {
        console.error('Error appending file to FormData:', e);
        formData.append('file', this.files[0]);
        console.log(`Appended file to FormData with key "file" (fallback)`);
      }
    } else {
      // For multiple files in auto-upload mode, we still need to use 'file' key
      // since the server expects it
      if (this.files.length === 1) {
        // Single file in multiple mode (common for auto-upload)
        formData.append('file', this.files[0], this.files[0].name);
        console.log('Appended single file in multiple mode with key "file"');
      } else {
        // Multiple files
        this.files.forEach(file => {
          formData.append('file', file);
        });
      }
    }

    // Add the input name as a type parameter for the server to identify the upload type
    formData.append('type', inputName);
    console.log('Appended type to FormData:', inputName);

    // Add submission_uuid if available (for submission tracking)
    const submissionUuidInput = document.querySelector('input[name="submission_uuid"]');
    if (submissionUuidInput && submissionUuidInput.value) {
      formData.append('submission_uuid', submissionUuidInput.value);
      console.log('Appended submission_uuid to FormData:', submissionUuidInput.value);
    } else {
      console.log('No submission_uuid found in form');
    }

    // Debug: Log what we're sending
    console.log('Uploading file:', this.files[0]);
    console.log('FormData entries:');
    let formDataEntryCount = 0;
    for (let [key, value] of formData.entries()) {
      formDataEntryCount++;
      console.log(`  ${key}:`, value);
    }
    console.log('Total FormData entries:', formDataEntryCount);

    // Check if FormData is properly constructed
    console.log('FormData has entries:', formData.entries().next().done === false);
    console.log('Files array length:', this.files.length);
    console.log('First file object:', this.files[0] ? {
      name: this.files[0].name,
      size: this.files[0].size,
      type: this.files[0].type,
      lastModified: this.files[0].lastModified,
    } : 'No file');

    // Update UI to show uploading state
    this.element.classList.add('uploading');
    this.element.classList.remove('error', 'success');

    // Show upload progress for single file mode
    if (!this.multiple && this.files.length === 1) {
      const existingProgress = this.element.querySelector('.upload-progress');
      if (!existingProgress) {
        const progressDiv = document.createElement('div');
        progressDiv.className = 'upload-progress';
        progressDiv.innerHTML = '<div class="progress-bar"></div>';
        this.element.appendChild(progressDiv);
        console.log('Created upload progress element');
      } else {
        console.log('Upload progress element already exists');
        // Make sure it's visible
        existingProgress.style.display = 'block';
      }
    }

    try {
      // Check if we should use fetch for debugging
      const useFetch = this.element.dataset.useFetch === 'true';

      if (useFetch) {
        console.log('Using fetch API instead of XMLHttpRequest');

        try {
          const response = await fetch(url, {
            method: 'POST',
            body: formData,
          });

          const responseText = await response.text();
          console.log('Fetch response:', response.status, responseText);

          if (response.ok) {
            const data = JSON.parse(responseText);

            // Update UI for success
            this.element.classList.remove('uploading');
            this.element.classList.add('success');

            return data.data || data;
          } else {
            throw new Error(`Upload failed: ${responseText}`);
          }
        } catch (error) {
          console.error('Fetch upload error:', error);
          throw error;
        }
      }

      // Use XMLHttpRequest for progress tracking
      const xhr = new XMLHttpRequest();
      this.activeXHR = xhr; // Store reference for potential cancellation

      // Track upload progress
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentComplete = Math.round((e.loaded / e.total) * 100);
          console.log(`📊 UPLOAD PROGRESS: ${percentComplete}% (${e.loaded}/${e.total} bytes)`);
          this.updateProgress(percentComplete);
          if (onProgress) onProgress(percentComplete);

          // When upload reaches 100%, show processing state
          if (percentComplete === 100) {
            console.log('✅ UPLOAD COMPLETE (100%) - Starting cloud storage processing...');
            this.showProcessingState();
          }
        }
      });

      // Create a promise to handle the async operation
      const uploadPromise = new Promise((resolve, reject) => {
        xhr.addEventListener('load', () => {
          console.log('Upload response:', xhr.status, xhr.responseText);

          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const response = JSON.parse(xhr.responseText);
              console.log('Parsed response:', response);

              if (response.success || response.success === undefined) {
                // Handle both old format (direct data) and new format (success/data)
                resolve(response.data || response);
              } else {
                reject(new Error(response.message || 'Upload failed'));
              }
            } catch (error) {
              console.error('Failed to parse response:', error);
              reject(new Error('Invalid server response: ' + xhr.responseText));
            }
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}: ${xhr.responseText}`));
          }
        });

        xhr.addEventListener('error', () => {
          reject(new Error('Network error during upload'));
        });

        xhr.addEventListener('abort', () => {
          reject(new Error('Upload cancelled'));
        });
      });

      // Send the request
      xhr.open('POST', url);

      // Don't set Content-Type header - let browser set it with boundary for multipart/form-data
      // xhr.setRequestHeader('Content-Type', 'multipart/form-data'); // DON'T DO THIS!

      // Final check before sending
      console.log('Final FormData check before send:');
      let hasFile = false;
      for (let [key, value] of formData.entries()) {
        console.log(`  ${key}: ${value instanceof File ? 'File(' + value.name + ')' : value}`);
        if (key === 'file' && value instanceof File) {
          hasFile = true;
        }
      }

      if (!hasFile) {
        console.error('WARNING: No file found in FormData before send!');
      }

      console.log('Sending XHR to:', url);
      xhr.send(formData);

      // Wait for upload to complete
      const result = await uploadPromise;
      this.activeXHR = null; // Clear reference after completion

      // Update UI for success
      this.element.classList.remove('uploading', 'processing');
      this.element.classList.add('success');

      // Show "Done!" with fade transition
      const text = this.element.querySelector('.upload-text, p');
      if (text) {
        // Remove pulsate if it's still there
        text.classList.remove('pulsate');
        text.classList.add('fade-transition');

        setTimeout(() => {
          text.innerHTML = 'Done!';
        }, 300);

        setTimeout(() => {
          text.classList.remove('fade-transition');
        }, 600);
      }

      // Hide processing state and clean up after showing success
      setTimeout(() => {
        this.hideProcessingState();

        // Hide the progress bar if still visible
        const progressBar = this.element.querySelector('.upload-progress');
        if (progressBar && progressBar.style.display !== 'none') {
          progressBar.style.transition = 'opacity 0.3s ease-out';
          progressBar.style.opacity = '0';
          setTimeout(() => {
            progressBar.style.display = 'none';
          }, 300);
        }

        // Restore original text with fade
        if (text && text.hasAttribute('data-original-text')) {
          text.classList.add('fade-transition');

          setTimeout(() => {
            const originalText = text.getAttribute('data-original-text');
            text.innerHTML = originalText;
            text.removeAttribute('data-original-text');
          }, 300);

          setTimeout(() => {
            text.classList.remove('fade-transition');
          }, 600);
        }
      }, 1500); // Show "Done!" for 1.5 seconds

      // Update notification to success state
      if (this.showNotifications && this.files[0]) {
        this.updateFileNotification(this.files[0].name, 'success');
        // Auto-dismiss success notifications after 3 seconds if not persistent
        if (!this.notificationPersistent) {
          setTimeout(() => {
            this.removeFileNotification(this.files[0].name);
          }, 3000);
        }
      }

      return result;

    } catch (error) {
      this.activeXHR = null; // Clear reference on error

      // Update UI for error
      this.element.classList.remove('uploading', 'processing');

      // Only show error state if not cancelled
      if (error.message !== 'Upload cancelled') {
        this.element.classList.add('error');
        this.showError('Upload failed: ' + error.message);
      } else {
        console.log('Upload was cancelled by user');
        // Clean up UI for cancelled state
        const text = this.element.querySelector('.upload-text, p');
        if (text && text.hasAttribute('data-original-text')) {
          text.innerHTML = text.getAttribute('data-original-text');
          text.removeAttribute('data-original-text');
          text.classList.remove('pulsate', 'fade-transition');
        }
      }

      // Hide processing state
      this.hideProcessingState();

      // Hide progress bar
      const progressBar = this.element.querySelector('.upload-progress');
      if (progressBar) {
        progressBar.style.display = 'none';
      }

      // Update notification to error state
      if (this.showNotifications && this.files[0] && error.message !== 'Upload cancelled') {
        this.updateFileNotification(this.files[0].name, 'error');
      }

      throw error;
    }
  }

  updateProgress (percent) {
    // Update parent drop zone background with overall completion % (green color)
    this.element.style.background = `linear-gradient(90deg, rgba(76, 175, 80, 0.1) 0%, rgba(76, 175, 80, 0.1) ${percent}%, transparent ${percent}%)`;

    // Update the progress bar in the element as well if it exists
    const progressBar = this.element.querySelector('.upload-progress .progress-bar');
    if (progressBar) {
      progressBar.style.width = percent + '%';
    }

    // Update single file item background if exists (green color)
    const fileItem = this.element.parentNode.querySelector('.single-file-item');
    if (fileItem) {
      fileItem.style.background = `linear-gradient(90deg, rgba(76, 175, 80, 0.1) 0%, rgba(76, 175, 80, 0.1) ${percent}%, transparent ${percent}%)`;
    }

    // Update notification progress if enabled
    if (this.showNotifications && this.files[0]) {
      this.updateFileNotification(this.files[0].name, 'uploading', percent);
    }
  }

  /**
   * Update batch upload progress bar based on all files' individual progress
   */
  updateBatchProgress () {
    if (!this.batchProgressBar || !this.batchFiles) {
      console.warn('⚠️ Cannot update batch progress: missing batchProgressBar or batchFiles');
      return;
    }

    // If all files were removed from batch, clean up and exit
    if (this.batchFiles.length === 0) {
      console.log('📊 All files removed from batch, cleaning up...');
      this.batchProgressBar.style.width = '0%';
      this.element.style.background = '';
      this.batchProgressBar = null;
      this.batchFiles = null;
      this.element.classList.remove('uploading', 'processing');
      return;
    }

    // Calculate total progress across files in the current batch
    let totalProgress = 0;
    const progressDetails = [];
    this.batchFiles.forEach((file, index) => {
      const fileProgress = file.uploadProgress || 0;
      totalProgress += fileProgress;
      progressDetails.push(`${file.name.substring(0, 20)}: ${fileProgress}%`);
    });

    // Average progress across batch files
    const overallProgress = Math.round(totalProgress / this.batchFiles.length);

    console.log(`📊 BATCH PROGRESS UPDATE: ${overallProgress}% overall | ${progressDetails.join(', ')}`);

    // Update progress bar and drop zone background
    this.batchProgressBar.style.width = overallProgress + '%';
    this.element.style.background = `linear-gradient(90deg, rgba(76, 175, 80, 0.1) 0%, rgba(76, 175, 80, 0.1) ${overallProgress}%, transparent ${overallProgress}%)`;
  }

  showProcessingState () {
    console.log('showProcessingState called');

    // Add processing class to the element
    this.element.classList.add('processing');
    this.element.classList.remove('uploading');
    console.log('Added processing class, removed uploading class');

    // Switch cloud icon to sync icon with rotation
    const icon = this.element.querySelector('i.fas');
    if (icon) {
      // Store original icon class if not already stored
      if (!icon.hasAttribute('data-original-icon')) {
        icon.setAttribute('data-original-icon', icon.className);
      }
      // Change to sync icon with rotation animation
      icon.className = 'fas fa-sync fa-spin';
      console.log('Changed icon to rotating sync');
    }

    // Hide the progress bar when moving to processing
    const progressContainer = this.element.querySelector('.upload-progress');
    if (progressContainer) {
      progressContainer.style.transition = 'opacity 0.3s ease-out';
      progressContainer.style.opacity = '0';
      setTimeout(() => {
        progressContainer.style.display = 'none';
      }, 300);
      console.log('Hiding progress bar for processing state');
    }

    // Update text to show processing state with fade transition
    const text = this.element.querySelector('.upload-text, p');
    if (text && !text.innerHTML.includes('Processing')) {
      // Don't save original if we're already in uploading state
      if (!text.hasAttribute('data-original-text')) {
        const originalText = text.innerHTML;
        text.setAttribute('data-original-text', originalText);
      }

      // Add fade transition
      text.classList.add('fade-transition');

      // Change text after fade out
      setTimeout(() => {
        text.innerHTML = 'Processing...';
        // Add pulsate animation for processing
        text.classList.add('pulsate');
      }, 300);

      // Remove fade animation class after completion, keep pulsate
      setTimeout(() => {
        text.classList.remove('fade-transition');
      }, 600);

      console.log('Updated text to show processing message with fade');
    } else {
      console.log('Text element not found or already showing processing');
    }

    // For single file mode, update the file item UI
    const fileItem = this.element.parentNode.querySelector('.single-file-item');
    if (fileItem) {
      console.log('Found single file item, updating UI...');
      const uploadBtn = fileItem.querySelector('.file-upload-btn');
      if (uploadBtn && uploadBtn.style.display !== 'none') {
        uploadBtn.style.display = 'none'; // Hide the upload button
        console.log('Hid upload button during processing');
      }

      // Keep the remove button visible for cancellation
      const removeBtn = fileItem.querySelector('.file-remove');
      if (removeBtn) {
        removeBtn.style.display = 'inline-flex'; // Keep visible for cancellation
      }

      // Update progress bar in file item
      const fileProgress = fileItem.querySelector('.file-progress');
      if (fileProgress) {
        const fileProgressBar = fileProgress.querySelector('.progress-bar');
        if (fileProgressBar) {
          fileProgressBar.style.width = '100%';
          fileProgressBar.classList.add('processing');
          console.log('Updated file item progress bar to processing state');
        }
      }
    } else {
      console.log('No single file item found');
    }

    // Update notification to processing state
    if (this.showNotifications && this.files[0]) {
      this.updateFileNotification(this.files[0].name, 'processing');
    }
  }

  hideProcessingState () {
    console.log('hideProcessingState called');

    // Remove processing class
    this.element.classList.remove('processing');
    console.log('Removed processing class from element');

    // Remove processing class from progress bar
    const progressBar = this.element.querySelector('.upload-progress .progress-bar');
    if (progressBar) {
      progressBar.classList.remove('processing');
      console.log('Removed processing class from progress bar');
    }

    // Restore original icon
    const icon = this.element.querySelector('i.fas');
    if (icon && icon.hasAttribute('data-original-icon')) {
      icon.className = icon.getAttribute('data-original-icon');
      icon.removeAttribute('data-original-icon');
      console.log('Restored original icon');
    }

    // Restore original text if saved
    const text = this.element.querySelector('.upload-text, p');
    if (text && text.hasAttribute('data-original-text')) {
      text.innerHTML = text.getAttribute('data-original-text');
      text.removeAttribute('data-original-text');
      console.log('Restored original text');
    }
  }

  // Clear all files
  clear () {
    // Cancel any active upload
    if (this.activeXHR) {
      console.log('Cancelling active upload during clear...');
      this.activeXHR.abort();
      this.activeXHR = null;
    }

    // Cancel all active batch uploads (keys are now file references)
    this.activeXHRs.forEach((xhr, file) => {
      console.log(`Cancelling batch upload for file: ${file.name}...`);
      xhr.abort();
    });
    this.activeXHRs.clear();

    // Clear all notifications
    if (this.showNotifications) {
      this.fileNotifications.forEach((notificationId, filename) => {
        if (window.StudioDome?.notifications?.hide) {
          window.StudioDome.notifications.hide(notificationId);
        }
      });
      this.fileNotifications.clear();
    }

    this.files = [];
    this.input.value = '';
    this.element.classList.remove('has-file', 'success', 'error', 'uploading', 'processing', 'is-valid', 'is-invalid');

    // Remove any single file items
    const singleFileItem = this.element.parentNode.querySelector('.single-file-item');
    if (singleFileItem) {
      singleFileItem.remove();
    }

    // Reset upload area text
    const text = this.element.querySelector('.upload-text, p');
    if (text && text.innerHTML.includes('Uploaded!')) {
      text.innerHTML = text.innerHTML.replace(/<i[^>]*><\/i>\s*/, '').replace(' - Uploaded!', '');
    }

    // Clear file list for multiple mode
    if (this.fileList) {
      this.fileList.innerHTML = '';
    }

    // Dispatch fileremove event to notify parent components
    this.element.dispatchEvent(new CustomEvent('fileremove', {
      bubbles: true,
      detail: { files: [] },
    }));
  }

  // Get files
  getFiles () {
    return this.files;
  }

  // Upload single file from multiple file list
  async uploadSingleFile (index) {
    if (index < 0 || index >= this.files.length) {
      console.error('Invalid file index:', index);
      return;
    }

    const file = this.files[index];
    const fileItem = this.fileList.querySelector(`[data-file-index="${index}"]`);
    if (!fileItem) {
      console.error('File item not found for index:', index);
      return;
    }

    const uploadBtn = fileItem.querySelector('.file-upload-btn');
    const removeBtn = fileItem.querySelector('.file-remove');
    const progressDiv = fileItem.querySelector('.file-progress');
    const progressBar = progressDiv.querySelector('.progress-bar');

    try {
      // Update UI for uploading state
      uploadBtn.style.display = 'none';
      removeBtn.style.display = 'none';
      progressDiv.style.display = 'block';
      fileItem.classList.add('uploading');

      // Create FormData for single file
      const formData = new FormData();
      formData.append('file', file, file.name);
      const inputName = this.element.dataset.inputName || this.input.name || 'file';
      formData.append('type', inputName);

      // Add CSRF token if available
      const csrfToken = document.querySelector('#csrf-token')?.value ||
        document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
      if (csrfToken) {
        formData.append('csrf_token', csrfToken);
        console.log('Added CSRF token to single file upload');
      }

      // Add upload path if specified (for GCS Manager)
      const uploadPath = this.element.dataset.uploadPath || this.element.getAttribute('data-upload-path');
      if (uploadPath !== null && uploadPath !== undefined) {
        formData.append('path', uploadPath);
        console.log('Added upload path to single file upload:', uploadPath || 'root');
      }

      // Add submission_uuid if available (for submission tracking)
      const submissionUuidInput = document.querySelector('input[name="submission_uuid"]');
      if (submissionUuidInput && submissionUuidInput.value) {
        formData.append('submission_uuid', submissionUuidInput.value);
      }

      // Add submission_id from data attribute (for admin submission uploads)
      const submissionId = this.element.dataset.submissionId;
      if (submissionId) {
        formData.append('submission_id', submissionId);
        console.log('Added submission_id to batch upload:', submissionId);
      }

      const uploadUrl = this.element.dataset.uploadUrl || '/api/public/upload-file';

      // Use XMLHttpRequest for progress tracking
      const xhr = new XMLHttpRequest();
      this.activeXHRs.set(file, xhr); // Use file reference as key, not index!

      // Track upload progress
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentComplete = Math.round((e.loaded / e.total) * 100);
          progressBar.style.width = percentComplete + '%';

          // When upload reaches 100%, show processing state
          if (percentComplete === 100) {
            progressBar.classList.add('processing');
            uploadBtn.innerHTML = '<i class="fas fa-spinner fa-spin"></i> Processing...';
          }
        }
      });

      // Create promise for async handling
      const uploadPromise = new Promise((resolve, reject) => {
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const response = JSON.parse(xhr.responseText);
              if (response.success || response.success === undefined) {
                resolve(response.data || response);
              } else {
                reject(new Error(response.message || 'Upload failed'));
              }
            } catch (error) {
              reject(new Error('Invalid server response'));
            }
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        });

        xhr.addEventListener('error', () => {
          reject(new Error('Network error during upload'));
        });
      });

      xhr.open('POST', uploadUrl);
      xhr.send(formData);

      const result = await uploadPromise;
      this.activeXHRs.delete(file); // Clear reference after completion (using file reference)

      // Update UI for success
      fileItem.classList.remove('uploading');
      fileItem.classList.add('uploaded');
      progressDiv.style.display = 'none';
      uploadBtn.innerHTML = '<i class="fas fa-check"></i> Uploaded';
      uploadBtn.classList.remove('primary');
      uploadBtn.classList.add('success');
      uploadBtn.style.display = 'inline-flex';
      uploadBtn.disabled = true;
      removeBtn.style.display = 'inline-flex';

      // Mark file as uploaded
      file.uploaded = true;

      // Dispatch custom event
      this.element.dispatchEvent(new CustomEvent('fileuploaded', {
        bubbles: true,
        detail: { file: file, result: result, index: index },
      }));

      return result;

    } catch (error) {
      this.activeXHRs.delete(file); // Clear reference on error (using file reference)

      // Update UI for error
      fileItem.classList.remove('uploading');
      progressDiv.style.display = 'none';
      removeBtn.style.display = 'inline-flex';

      // Only show error state if not cancelled
      if (error.message !== 'Upload cancelled') {
        fileItem.classList.add('error');
        uploadBtn.style.display = 'inline-flex';
        console.error('Upload failed for file:', file.name, error);
        this.showError(`Upload failed for ${file.name}: ${error.message}`);
      } else {
        console.log(`Upload cancelled for file: ${file.name}`);
        // Reset UI to pre-upload state
        uploadBtn.style.display = 'inline-flex';
        fileItem.classList.remove('error');
      }

      throw error;
    }
  }

  // Upload all files
  async uploadAllFiles () {
    const uploadAllBtn = this.fileList.querySelector('.upload-all-btn');
    if (!uploadAllBtn) return;

    // Filter out already uploaded files
    const filesToUpload = this.files
      .map((file, index) => ({ file, index }))
      .filter(item => !item.file.uploaded);

    if (filesToUpload.length === 0) {
      this.showError('All files have already been uploaded');
      return;
    }

    // Disable upload all button
    uploadAllBtn.disabled = true;
    uploadAllBtn.innerHTML = '<i class="fas fa-spinner fa-spin"></i>';

    // Show overall progress container
    let overallProgressContainer = this.element.querySelector('.upload-progress');
    if (!overallProgressContainer) {
      overallProgressContainer = document.createElement('div');
      overallProgressContainer.className = 'upload-progress';
      overallProgressContainer.innerHTML = '<div class="progress-bar"></div>';
      this.element.appendChild(overallProgressContainer);
    }
    overallProgressContainer.style.display = 'block';

    const overallProgressBar = overallProgressContainer.querySelector('.progress-bar');
    const totalFiles = filesToUpload.length;
    let completedFiles = 0;

    let successCount = 0;
    let errorCount = 0;

    // Upload files in parallel batches (max 3 at a time for better performance)
    const batchSize = 3;
    for (let i = 0; i < filesToUpload.length; i += batchSize) {
      const batch = filesToUpload.slice(i, i + batchSize);
      const batchPromises = batch.map(async ({ file, index }) => {
        try {
          await this.uploadSingleFileInBatch(index);
          successCount++;
          completedFiles++;

          // Update overall progress
          const overallProgress = Math.round((completedFiles / totalFiles) * 100);
          overallProgressBar.style.width = overallProgress + '%';

          return { success: true, index };
        } catch (error) {
          errorCount++;
          completedFiles++;

          // Update overall progress even for failed files
          const overallProgress = Math.round((completedFiles / totalFiles) * 100);
          overallProgressBar.style.width = overallProgress + '%';

          console.error(`Failed to upload ${file.name}:`, error);
          return { success: false, index, error };
        }
      });

      // Wait for current batch to complete before starting next
      await Promise.all(batchPromises);
    }

    // Hide overall progress
    overallProgressContainer.style.display = 'none';

    // Update upload all button
    if (errorCount === 0) {
      uploadAllBtn.innerHTML = '<i class="fas fa-check"></i> All Files Uploaded';
      uploadAllBtn.classList.remove('primary');
      uploadAllBtn.classList.add('success');
    } else {
      uploadAllBtn.innerHTML = `<i class="fas fa-exclamation-triangle"></i> ${successCount} Uploaded, ${errorCount} Failed`;
      uploadAllBtn.disabled = false;
      uploadAllBtn.classList.add('warning');
    }

    // Check if all files are uploaded
    const allUploaded = this.files.every(file => file.uploaded);
    if (allUploaded) {
      uploadAllBtn.disabled = true;
    }
  }

  // Upload single file in batch with individual progress tracking
  async uploadSingleFileInBatch (index) {
    if (index < 0 || index >= this.files.length) {
      console.error('Invalid file index:', index);
      return;
    }

    const file = this.files[index];

    // Mark file as uploading and re-render to move it to top
    file.uploadStatus = 'uploading';
    file.uploadProgress = 0;
    this.renderFileList();

    // Use stable file ID instead of index to find DOM element
    let fileItem = this.fileList.querySelector(`[data-file-id="${file._fileUploadId}"]`);
    if (!fileItem) {
      console.error('File item not found for file:', file.name, file._fileUploadId);
      return;
    }

    const uploadBtn = fileItem.querySelector('.file-upload-btn');
    const removeBtn = fileItem.querySelector('.file-remove');
    const progressDiv = fileItem.querySelector('.file-progress');
    const progressBar = progressDiv?.querySelector('.progress-bar');

    try {
      // Update UI for uploading state with progress bar
      if (uploadBtn) uploadBtn.style.display = 'none';
      if (removeBtn) removeBtn.style.display = 'none';
      if (progressDiv) progressDiv.style.display = 'block';

      // Create FormData for single file
      const formData = new FormData();
      formData.append('file', file, file.name);
      const inputName = this.element.dataset.inputName || this.input.name || 'file';
      formData.append('type', inputName);

      // Add CSRF token if available
      const csrfToken = document.querySelector('#csrf-token')?.value ||
        document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
      if (csrfToken) {
        formData.append('csrf_token', csrfToken);
        console.log('Added CSRF token to batch upload');
      }

      // Add upload path if specified (for GCS Manager)
      const uploadPath = this.element.dataset.uploadPath || this.element.getAttribute('data-upload-path');
      if (uploadPath !== null && uploadPath !== undefined) {
        formData.append('path', uploadPath);
        console.log('Added upload path to batch upload:', uploadPath || 'root');
      }

      // Add submission_uuid if available (for submission tracking)
      const submissionUuidInput = document.querySelector('input[name="submission_uuid"]');
      if (submissionUuidInput && submissionUuidInput.value) {
        formData.append('submission_uuid', submissionUuidInput.value);
      }

      // Add submission_id from data attribute (for admin submission uploads)
      const submissionId = this.element.dataset.submissionId;
      if (submissionId) {
        formData.append('submission_id', submissionId);
        console.log('Added submission_id to batch upload:', submissionId);
      }

      const uploadUrl = this.element.dataset.uploadUrl || '/api/public/upload-file';

      // Use XMLHttpRequest for progress tracking
      const xhr = new XMLHttpRequest();
      this.activeXHRs.set(file, xhr); // Use file reference as key, not index!

      // Track upload progress and update background gradient (green color)
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentComplete = Math.round((e.loaded / e.total) * 100);
          file.uploadProgress = percentComplete;

          // Update file item background gradient without re-rendering entire list
          // Use stable file ID to find element reliably even after removals
          const currentFileItem = this.fileList.querySelector(`[data-file-id="${file._fileUploadId}"]`);
          if (currentFileItem) {
            currentFileItem.style.background = `linear-gradient(90deg, rgba(76, 175, 80, 0.1) 0%, rgba(76, 175, 80, 0.1) ${percentComplete}%, transparent ${percentComplete}%)`;
          } else {
            console.warn(`Could not find file item for progress update: ${file.name} (${file._fileUploadId})`);
          }

          // Update overall batch progress in real-time
          this.updateBatchProgress();
        }
      });

      // Create promise for async handling
      const uploadPromise = new Promise((resolve, reject) => {
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const response = JSON.parse(xhr.responseText);
              if (response.success || response.success === undefined) {
                resolve(response.data || response);
              } else {
                reject(new Error(response.message || 'Upload failed'));
              }
            } catch (error) {
              reject(new Error('Invalid server response'));
            }
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        });

        xhr.addEventListener('error', () => {
          reject(new Error('Network error during upload'));
        });

        xhr.addEventListener('abort', () => {
          reject(new Error('Upload cancelled'));
        });
      });

      xhr.open('POST', uploadUrl);
      xhr.send(formData);

      const result = await uploadPromise;
      this.activeXHRs.delete(file); // Clear reference (using file reference)

      console.log(`✅ File ${index} (${file.name}) completed successfully`);

      // Mark file as uploaded and update batch progress
      file.uploadStatus = 'uploaded';
      file.uploadProgress = 100;
      file.uploaded = true;

      // Update batch progress to reflect this file is complete
      console.log(`Updating batch progress after file ${index} completion...`);
      this.updateBatchProgress();

      this.renderFileList();

      // Dispatch custom event
      this.element.dispatchEvent(new CustomEvent('fileuploaded', {
        bubbles: true,
        detail: { file: file, result: result, index: index },
      }));

      return result;

    } catch (error) {
      this.activeXHRs.delete(file); // Clear reference (using file reference)

      // Handle cancellation vs error differently
      if (error.message === 'Upload cancelled') {
        // Mark as cancelled and set progress to 100 so it doesn't affect batch progress
        file.uploadStatus = 'cancelled';
        file.uploadProgress = 100;
        console.log(`Upload cancelled for file: ${file.name}`);
      } else {
        // Mark file as error
        file.uploadStatus = 'error';
        file.uploadProgress = 100; // Mark as "complete" for progress tracking purposes
        console.error('Upload failed for file:', file.name, error);
        this.showError(`Upload failed for ${file.name}: ${error.message}`);
      }

      // Update batch progress to reflect this file is done (cancelled or failed)
      this.updateBatchProgress();

      // Re-render to show updated status
      this.renderFileList();

      throw error;
    }
  }

  /**
   * Create a notification for a file
   */
  createFileNotification (file, state) {
    if (!window.StudioDome?.notify) return null;

    const messages = {
      preparing: 'Preparing for upload...',
      uploading: '',
      processing: 'Processing...',
      success: 'Upload completed!',
      error: 'Upload failed',
    };

    const types = {
      preparing: 'info',
      uploading: 'primary',
      processing: 'secondary',
      success: 'success',
      error: 'error',
    };

    const options = {
      type: types[state] || 'info',
      duration: this.notificationPersistent ? 0 : 5000,
      position: this.notificationPosition,
      icon: state === 'success' ? 'fa-check-circle' :
        state === 'error' ? 'fa-times-circle' :
          state === 'processing' ? 'fa-cog fa-spin' : 'fa-upload',
      showProgress: state === 'uploading',
      actions: [],
    };

    // Add navigation action for success/error states
    if ((state === 'success' || state === 'error') && window.submitTitle?.wizard) {
      const stepNumber = this.getUploadStepNumber();
      if (stepNumber) {
        options.actions.push({
          text: state === 'error' ? 'Go to Upload' : 'View Upload',
          onClick: () => this.navigateToStep(stepNumber),
          class: 'button secondary small',
        });
      }
    }

    // Add retry action for error state
    if (state === 'error') {
      options.actions.unshift({
        text: 'Retry',
        onClick: () => this.retryFileUpload(file.name),
        class: 'button primary small',
        dismiss: false,
      });
    }

    const message = {
      title: file.name,
      body: messages[state],
    };

    return window.StudioDome.notify(message, options);
  }

  /**
   * Update an existing file notification
   */
  updateFileNotification (filename, state, progress = null) {
    const notificationId = this.fileNotifications.get(filename);

    if (!notificationId) {
      console.warn('No notification ID found for file:', filename, 'Available:', Array.from(this.fileNotifications.keys()));
      // If no notification exists and we're in a valid state, create one
      if (this.showNotifications && window.StudioDome?.notify) {
        const file = this.files.find(f => f.name === filename);
        if (file) {
          const newNotificationId = this.createFileNotification(file, state);
          if (newNotificationId) {
            this.fileNotifications.set(filename, newNotificationId);
          }
        }
      }
      return;
    }

    if (!window.StudioDome?.notifications?.update) {
      console.warn('Notification update method not available');
      return;
    }

    const messages = {
      preparing: 'Preparing for upload...',
      uploading: progress !== null ? `${Math.round(progress)}%` : '',
      processing: 'Processing...',
      success: 'Upload completed!',
      error: 'Upload failed',
    };

    const types = {
      preparing: 'info',
      uploading: 'primary',
      processing: 'secondary',
      success: 'success',
      error: 'error',
    };

    // The update method expects a string message, not an object
    const message = `${filename}: ${messages[state]}`;

    const options = {
      type: types[state],
    };

    // Update icon based on state
    if (state === 'success') {
      options.icon = 'fa-check-circle';
    } else if (state === 'error') {
      options.icon = 'fa-times-circle';
    } else if (state === 'processing') {
      options.icon = 'fa-cog fa-spin';
    } else if (state === 'uploading') {
      options.icon = 'fa-upload';
    }

    window.StudioDome.notifications.update(notificationId, message, options);
  }

  /**
   * Remove a file notification
   */
  removeFileNotification (filename) {
    const notificationId = this.fileNotifications.get(filename);
    if (notificationId && window.StudioDome?.notifications?.hide) {
      window.StudioDome.notifications.hide(notificationId);
    }
    this.fileNotifications.delete(filename);
  }

  /**
   * Get the step number where file uploads are handled (step 3 for submit-title)
   */
  getUploadStepNumber () {
    // Try to detect if we're in the submit-title form
    if (document.getElementById('titleSubmissionContainer')) {
      return 3; // Review Materials step
    }
    return null;
  }

  /**
   * Retry upload for a specific file
   */
  retryFileUpload (filename) {
    const file = this.files.find(f => f.name === filename);
    if (!file) return;

    const fileIndex = this.files.indexOf(file);
    if (fileIndex >= 0) {
      this.uploadSingleFile(fileIndex);
    }
  }
}

/**
 * Auto-initialize all FileUpload instances
 * Called on DOMContentLoaded and can be called manually after AJAX updates
 * Uses lazy initialization for elements in hidden containers (like step forms)
 */
FileUpload.initAll = function() {
  const elements = document.querySelectorAll('.file-upload-area');

  // Only log if there are actually dropzones to initialize
  if (elements.length === 0) {
    return;
  }

  console.log('[FileUpload] Found', elements.length, 'dropzones');

  // Check if element is in a hidden step (for lazy init)
  const isInHiddenStep = (el) => {
    const stepCard = el.closest('.step-card, [data-step]');
    return stepCard && stepCard.classList.contains('hidden');
  };

  // Initialize visible dropzones immediately, defer hidden ones
  const visibleElements = [];
  const hiddenElements = [];

  elements.forEach((element) => {
    if (element.fileUpload) {
      // Already initialized
      return;
    }
    if (isInHiddenStep(element)) {
      hiddenElements.push(element);
    } else {
      visibleElements.push(element);
    }
  });

  // Initialize visible dropzones immediately
  console.log('[FileUpload] Initializing', visibleElements.length, 'visible dropzones');
  visibleElements.forEach((element) => {
    new FileUpload(element);
  });

  // Use IntersectionObserver for hidden dropzones (lazy init when step becomes visible)
  if (hiddenElements.length > 0 && 'IntersectionObserver' in window) {
    console.log('[FileUpload] Deferring', hiddenElements.length, 'hidden dropzones for lazy init');

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !entry.target.fileUpload) {
          console.log('[FileUpload] Lazy initializing dropzone on visibility');
          new FileUpload(entry.target);
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1 });

    hiddenElements.forEach(el => observer.observe(el));
  } else if (hiddenElements.length > 0) {
    // Fallback: init all after a delay if IntersectionObserver not available
    setTimeout(() => {
      hiddenElements.forEach((element) => {
        if (!element.fileUpload) {
          new FileUpload(element);
        }
      });
    }, 1000);
  }
};

/**
 * Clean up a specific FileUpload instance or all instances
 * @param {HTMLElement} element - Specific element to clean up, or null for all
 */
FileUpload.cleanup = function(element = null) {
  if (element) {
    // Clean up specific instance
    if (element.fileUpload) {
      element.fileUpload.unbindEvents();
      delete element.fileUpload;
      console.log('[FileUpload] Cleaned up instance:', element);
    }
  } else {
    // Clean up all instances
    const elements = document.querySelectorAll('.file-upload-area');
    elements.forEach(el => {
      if (el.fileUpload) {
        el.fileUpload.unbindEvents();
        delete el.fileUpload;
      }
    });
    console.log('[FileUpload] Cleaned up all instances');
  }
};

// Auto-initialize - call immediately, wrapped in setTimeout to ensure DOM is ready
setTimeout(() => {
  FileUpload.initAll();
}, 0);

// Export for use in other modules
if (typeof module !== 'undefined' && module.exports) {
  module.exports = FileUpload;
}